import { P } from '../utils/permissions';

const SIMULATED_STRIPE_ID = 'acct_dev_state_lab';
const SIMULATED_FORM_ID = 'dev_state_lab_form';

export const PLAN_OPTIONS = ['STARTER', 'BASE', 'PLUS', 'PRO', 'ENTERPRISE'];
export const ENTITLEMENT_OVERRIDE_OPTIONS = [
  'legacy_starter',
  'legacy_custom',
  'grandfathered_base',
  'plus_grandfathered',
];
export const ONBOARDING_STEP_OPTIONS = [
  'invite_team_pre_form',
  'create_form',
  'stripe_connect',
  'script_install',
  'awaiting_stripe_charges',
  'checklist',
  'education',
  'complete',
];
export const ROLE_OPTIONS = [
  { value: 'org:admin', label: 'Admin' },
  { value: 'org:finance', label: 'Finance' },
  { value: 'org:editor', label: 'Editor' },
  { value: 'org:marketing', label: 'Marketing' },
  { value: 'org:member', label: 'Member' },
  { value: 'custom:marketing_manager', label: 'Custom marketing' },
];

const marketingPermissions = [
  P.MANAGE_MARKETING,
  P.CREATE_BROADCASTS,
  P.VIEW_BROADCASTS,
  P.EDIT_BROADCASTS,
  P.CREATE_SEGMENTS,
  P.VIEW_SEGMENTS,
  P.EDIT_SEGMENTS,
];

const stripePendingPatch = {
  defaultPaymentProcessor: 'STRIPE',
  stripeAccountID: SIMULATED_STRIPE_ID,
  stripeDetails: {
    accountId: SIMULATED_STRIPE_ID,
    detailsSubmitted: true,
    chargesEnabled: false,
    payoutsEnabled: false,
  },
};

const stripeEnabledPatch = {
  defaultPaymentProcessor: 'STRIPE',
  stripeAccountID: SIMULATED_STRIPE_ID,
  stripeDetails: {
    accountId: SIMULATED_STRIPE_ID,
    detailsSubmitted: true,
    chargesEnabled: true,
    payoutsEnabled: true,
  },
};

const finixPatch = {
  defaultPaymentProcessor: 'FINIX',
  stripeAccountID: null,
  finixDetails: {
    merchantId: 'MUdevstatelab',
    identityId: 'IDdevstatelab',
  },
};

const processorUnsetPatch = {
  defaultPaymentProcessor: null,
  stripeAccountID: null,
  stripeDetails: {
    accountId: null,
    detailsSubmitted: null,
    chargesEnabled: null,
    payoutsEnabled: null,
  },
  finixDetails: null,
};

// An anonymized, representative legacy fee schedule stored at the merchant's
// top level. The entitlement resolver no longer reads billingDetails.feeOverride.
const productionStyleFeeAmounts = {
  platformPercentFee: '0.05',
  processingPercentFee: '0.03',
  platformFixedFee: '0',
  processingFixedFee: '0',
};

const paymentStatusFixture = (id, title, details, fixture = {}) => ({
  id: `payment-status-${id}`, group: 'Payment status', title,
  description: 'Browser verification fixture. No Stripe or database changes.',
  state: { merchantPatch: { ...stripePendingPatch, stripeStatusFixture: { liveStatus: null, checkFailed: false, syncDelayed: false, ...fixture },
    stripeDetails: { ...stripePendingPatch.stripeDetails, detailsSubmitted: false,
      ...details, statusSnapshot: { chargesEnabled: details.chargesEnabled ?? false, payoutsEnabled: details.payoutsEnabled ?? false, detailsSubmitted: details.detailsSubmitted ?? false, requirementsCurrentDeadline: null, requirementsCurrentlyDue: [], requirementsPastDue: [], requirementsPendingVerification: [], requirementsDisabledReason: null, lastCheckedAt: null, ...details.statusSnapshot } },
  } },
});

export const DEV_STATE_PRESETS = [
  paymentStatusFixture('fresh', 'Payments: fresh setup', {}),
  paymentStatusFixture('submitted', 'Payments: submitted', { detailsSubmitted: true }),
  paymentStatusFixture('review', 'Payments: review pending', { detailsSubmitted: true, statusSnapshot: { requirementsPendingVerification: ['company.verification.document'] } }),
  paymentStatusFixture('requirements', 'Payments: information required', { detailsSubmitted: true, statusSnapshot: { requirementsCurrentlyDue: ['company.tax_id'], requirementsCurrentDeadline: 1790000000 } }),
  paymentStatusFixture('payouts', 'Payments enabled, payouts disabled', { chargesEnabled: true, payoutsEnabled: false }),
  paymentStatusFixture('restricted', 'Payments: restricted', { detailsSubmitted: true, statusSnapshot: { requirementsDisabledReason: 'rejected.other' } }),
  paymentStatusFixture('syncing', 'Payments: activating', {}, { liveStatus: { chargesEnabled: true } }),
  paymentStatusFixture('delayed', 'Payments: activation delayed', {}, { liveStatus: { chargesEnabled: true }, syncDelayed: true }),
  paymentStatusFixture('stale', 'Payments: reconnecting', { chargesEnabled: true, payoutsEnabled: true }, { checkFailed: true }),

  {
    id: 'fresh-signup-no-org',
    group: 'Signup',
    title: 'Fresh signup',
    description: 'No org membership and signup incomplete.',
    state: {
      signup: { hasExistingOrg: false, signupComplete: false },
      auth: null,
      readOnly: null,
      merchantPatch: {},
    },
  },
  {
    id: 'invite-team',
    group: 'Onboarding',
    title: 'Invite team',
    description: 'First blocked activation step.',
    state: {
      signup: { hasExistingOrg: true, signupComplete: true },
      merchantPatch: {
        onboardingCurrentStep: 'invite_team_pre_form',
        hasWebsite: true,
        driverFlowsCompleted: { preFormInvite: false },
      },
    },
  },
  {
    id: 'create-form',
    group: 'Onboarding',
    title: 'Create form',
    description: 'Guided form creation overlay.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'create_form',
        hasWebsite: true,
        lastPublishedFormId: null,
        driverFlowsCompleted: { preFormInvite: true, guidedFormTour: false },
      },
    },
  },
  {
    id: 'base-intent',
    group: 'Billing',
    title: 'Base intent',
    description: 'Paid plan selected before checkout succeeds.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'invite_team_pre_form',
        onboardingSelectedPlan: 'BASE',
        billingDetails: { plan: 'STARTER' },
      },
    },
  },
  {
    id: 'plus-intent',
    group: 'Billing',
    title: 'Plus intent',
    description: 'Plus selected before subscription webhook.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'invite_team_pre_form',
        onboardingSelectedPlan: 'PLUS',
        billingDetails: { plan: 'STARTER' },
      },
    },
  },
  {
    id: 'stripe-connect',
    group: 'Onboarding',
    title: 'Stripe connect',
    description: 'Stripe onboarding required.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'stripe_connect',
        hasWebsite: true,
        lastPublishedFormId: SIMULATED_FORM_ID,
        driverFlowsCompleted: { guidedFormTour: true },
        defaultPaymentProcessor: null,
        stripeAccountID: null,
      },
    },
  },
  {
    id: 'script-install',
    group: 'Onboarding',
    title: 'Script install',
    description: 'Website path script step.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'script_install',
        hasWebsite: true,
        lastPublishedFormId: SIMULATED_FORM_ID,
        driverFlowsCompleted: { guidedFormTour: true },
        ...stripePendingPatch,
      },
    },
  },
  {
    id: 'awaiting-charges',
    group: 'Onboarding',
    title: 'Awaiting charges',
    description: 'Stripe review and first charges pending.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'awaiting_stripe_charges',
        hasWebsite: true,
        lastPublishedFormId: SIMULATED_FORM_ID,
        driverFlowsCompleted: {
          activation: false,
          activationApprovalModalsComplete: false,
          activationProductTour: false,
          guidedFormTour: true,
        },
        ...stripePendingPatch,
      },
    },
  },
  {
    id: 'checklist',
    group: 'Onboarding',
    title: 'Checklist',
    description: 'Post-activation checklist.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'checklist',
        lastPublishedFormId: SIMULATED_FORM_ID,
        driverFlowsCompleted: { activation: true },
        ...stripeEnabledPatch,
      },
    },
  },
  {
    id: 'education',
    group: 'Onboarding',
    title: 'Education',
    description: 'Education step after checklist.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'education',
        lastPublishedFormId: SIMULATED_FORM_ID,
        driverFlowsCompleted: { activation: true },
        ...stripeEnabledPatch,
      },
    },
  },
  {
    id: 'complete',
    group: 'Onboarding',
    title: 'Complete',
    description: 'Fully onboarded org.',
    state: {
      merchantPatch: {
        onboardingCurrentStep: 'complete',
        driverFlowsCompleted: { activation: true },
      },
    },
  },
  {
    id: 'starter',
    group: 'Billing',
    title: 'Starter',
    description: 'Starter tier with starter override.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'STARTER',
          entitlementOverrides: 'legacy_starter',
          coveredCommitment: null,
        },
      },
    },
  },
  {
    id: 'implicit-starter',
    group: 'Billing',
    title: 'Implicit Starter',
    description: 'Current production default with no stored billing plan.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: null,
          entitlementOverrides: null,
          coveredCommitment: null,
        },
      },
    },
  },
  {
    id: 'starter-fee-schedule',
    group: 'Billing',
    title: 'Starter fee schedule',
    description: 'Stored Starter plan with a top-level legacy fee map.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'STARTER',
          entitlementOverrides: null,
          coveredCommitment: null,
        },
        feeAmounts: productionStyleFeeAmounts,
      },
    },
  },
  {
    id: 'base-marketing',
    group: 'Billing',
    title: 'Base marketing',
    description: 'Marketing surfaces unlocked.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'BASE',
          entitlementOverrides: null,
          coveredCommitment: true,
        },
      },
    },
  },
  {
    id: 'plus-crm',
    group: 'Billing',
    title: 'Plus CRM',
    description: 'Plus tier feature set.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'PLUS',
          entitlementOverrides: null,
          coveredCommitment: true,
        },
      },
    },
  },
  {
    id: 'legacy-custom',
    group: 'Billing',
    title: 'Legacy custom, fee gap',
    description: 'Grandfathered bundle without its negotiated fee map.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'STARTER',
          entitlementOverrides: 'legacy_custom',
        },
      },
    },
  },
  {
    id: 'legacy-custom-fees',
    group: 'Billing',
    title: 'Legacy custom, top-level fees',
    description: 'Production-shaped fee map that exposes the dashboard projection gap.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'STARTER',
          entitlementOverrides: 'legacy_custom',
        },
        feeAmounts: productionStyleFeeAmounts,
      },
    },
  },
  {
    id: 'custom-plan-sentinel',
    group: 'Billing',
    title: 'Custom plan sentinel',
    description: "Historical migration shape: plan:'CUSTOM', no override.",
    state: {
      merchantPatch: {
        // Retained as a compatibility fixture. Current production records use
        // STARTER or no stored plan; this sentinel is no longer a current shape.
        billingDetails: {
          plan: 'CUSTOM',
        },
      },
    },
  },
  {
    id: 'enterprise-missing-fee',
    group: 'Billing',
    title: 'Enterprise catalog edge',
    description: 'Enterprise plan without a negotiated billing profile.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'ENTERPRISE',
          entitlementOverrides: null,
        },
      },
    },
  },
  {
    id: 'enterprise-fee',
    group: 'Billing',
    title: 'Enterprise, top-level fees',
    description: 'Compatibility fee map that exposes the dashboard projection gap.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'ENTERPRISE',
          entitlementOverrides: 'legacy_custom',
        },
        feeAmounts: productionStyleFeeAmounts,
      },
    },
  },
  {
    id: 'stripe-pending',
    group: 'Processor',
    title: 'Stripe pending',
    description: 'Stripe account present, charges disabled.',
    state: { merchantPatch: stripePendingPatch },
  },
  {
    id: 'stripe-enabled',
    group: 'Processor',
    title: 'Stripe enabled',
    description: 'Stripe account fully enabled.',
    state: { merchantPatch: stripeEnabledPatch },
  },
  {
    id: 'stripe-account-no-processor',
    group: 'Processor',
    title: 'Stripe account, no marker',
    description: 'Observed transition: Stripe account exists before its processor marker.',
    state: {
      merchantPatch: {
        defaultPaymentProcessor: null,
        stripeAccountID: SIMULATED_STRIPE_ID,
        stripeDetails: {
          accountId: SIMULATED_STRIPE_ID,
          detailsSubmitted: true,
          chargesEnabled: true,
          payoutsEnabled: true,
        },
      },
    },
  },
  {
    id: 'finix',
    group: 'Processor',
    title: 'Finix',
    description: 'Finix default processor.',
    state: { merchantPatch: finixPatch },
  },
  {
    id: 'processor-unset',
    group: 'Processor',
    title: 'No processor marker',
    description: 'Observed legacy record without a configured payment processor.',
    state: { merchantPatch: processorUnsetPatch },
  },
  {
    id: 'finance-role',
    group: 'User',
    title: 'Finance role',
    description: 'Built-in finance permissions.',
    state: {
      auth: { effectiveRole: 'org:finance', customRole: null },
      readOnly: false,
    },
  },
  {
    id: 'editor-role',
    group: 'User',
    title: 'Editor role',
    description: 'Built-in editor permissions.',
    state: {
      auth: { effectiveRole: 'org:editor', customRole: null },
      readOnly: false,
    },
  },
  {
    id: 'read-only',
    group: 'User',
    title: 'Read-only',
    description: 'Read-only dashboard mode.',
    state: {
      auth: { effectiveRole: 'org:member', customRole: null },
      readOnly: true,
    },
  },
  {
    id: 'entitled-no-marketing-rbac',
    group: 'User',
    title: 'Entitled, no RBAC',
    description: 'Base plan with marketing permissions denied.',
    state: {
      merchantPatch: { billingDetails: { plan: 'BASE', entitlementOverrides: null } },
      auth: {
        effectiveRole: 'org:editor',
        permissionOverrides: { deny: marketingPermissions },
      },
      readOnly: false,
    },
  },
  {
    id: 'rbac-no-entitlement',
    group: 'User',
    title: 'RBAC, no entitlement',
    description: 'Marketing RBAC allowed on Starter.',
    state: {
      merchantPatch: {
        billingDetails: {
          plan: 'STARTER',
          entitlementOverrides: 'legacy_starter',
        },
      },
      auth: {
        effectiveRole: 'org:admin',
        permissionOverrides: { allow: marketingPermissions },
      },
      readOnly: false,
    },
  },
];

export function getProcessorPatch(value) {
  if (value === 'STRIPE_PENDING') return stripePendingPatch;
  if (value === 'STRIPE_ENABLED') return stripeEnabledPatch;
  if (value === 'FINIX') return finixPatch;
  return processorUnsetPatch;
}

export function getAuthPatchForRole(value) {
  if (value === 'custom:marketing_manager') {
    return {
      effectiveRole: 'custom:marketing_manager',
      customRole: 'marketing_manager',
      rolePermissions: {
        'custom:marketing_manager': marketingPermissions,
      },
    };
  }

  return {
    effectiveRole: value,
    customRole: null,
    rolePermissions: null,
    permissionOverrides: null,
  };
}
